import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./full-fleet.scss";
import Header from "../_Common/header";
import SectionHead from "../_Common/SectionHead";
import { Container, Nav, Tab } from "react-bootstrap";
import Aos from "aos";
import useSeo from "../../hooks/useSeo";

import boatIcon from "../../assets/img/home/our-fleet/icons/boat.png";

import fleetInventory from "../../json/FullFleet/fleet-inventory.json";

// Data Construction - Processed from JSON
const fleetData = Object.keys(fleetInventory).reduce((acc, category) => {
  acc[category] = fleetInventory[category].map((boat) => ({
    ...boat,
    image: boat.image ? `/${boat.image}` : boatIcon,
  }));
  return acc;
}, {});

// `label` is the tab text, `singular` names one vessel on its card, and
// `blurb` gives each category a short plain-English explainer.
const tabs = [
  {
    id: "sail_boats",
    name: "Sail Boats",
    singular: "Sail Boat",
    blurb:
      "Small, wind-powered boats for hands-on sailing in Mumbai harbour. Best for couples and small groups who want to feel the sail rather than sit back.",
  },
  {
    id: "sail_yachts",
    name: "Sail Yachts",
    singular: "Sail Yacht",
    blurb:
      "Larger sailing yachts with deck space, shade and cabins. Quiet, wind-powered cruising from Gateway of India — suited to birthdays, anniversaries and sunset sails.",
  },
  {
    id: "motor_yachts",
    name: "Motor Yachts",
    singular: "Motor Yacht",
    blurb:
      "Engine-powered luxury yachts with air-conditioned interiors. The fastest and most spacious option for parties, corporate groups and Elephanta Island trips.",
  },
  {
    id: "speed_boats",
    name: "Speed Boats",
    singular: "Speed Boat",
    blurb:
      "Quick, open boats for short thrill rides and point-to-point transfers — Gateway to Mandwa, Elephanta Caves or Mora / JNPT.",
  },
  {
    id: "ferry_boats",
    name: "Ferry Boats",
    singular: "Ferry Boat",
    blurb:
      "High-capacity vessels for large celebrations and group crossings between Gateway of India, Mandwa and Mora / JNPT.",
  },
];

const DEFAULT_TAB = "sail_boats";

export default function FullFleet() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [aosKey, setAosKey] = useState(0);
  const [activeTab, setActiveTab] = useState(
    () => searchParams.get("category") || DEFAULT_TAB
  );

  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam && fleetData[categoryParam]) {
      setActiveTab(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => Aos.refreshHard(), 100);
    return () => clearTimeout(timer);
  }, [aosKey]);

  const activeCategory =
    tabs.find((tab) => tab.id === activeTab) || tabs[0];
  const currentItems = useMemo(() => fleetData[activeTab] || [], [activeTab]);

  const guestRange = useMemo(() => {
    if (currentItems.length === 0) return null;
    const counts = currentItems.map((boat) => boat.guests);
    const min = Math.min(...counts);
    const max = Math.max(...counts);
    return min === max ? `${min}` : `${min}–${max}`;
  }, [currentItems]);

  useSeo({
    title: `${activeCategory.name} on Charter in Mumbai | Book Sailing Mumbai`,
    description: `${currentItems.length} ${activeCategory.name.toLowerCase()} available on charter from Gateway of India, Mumbai${guestRange ? `, seating ${guestRange} guests` : ""
      }. Compare vessels, routes and capacity, then book with a certified crew.`,
    canonical: `https://booksailingmumbai.co.in/full-fleet?category=${activeTab}`,
  });

  const handleTabSelect = (key) => {
    setActiveTab(key);
    setAosKey((prev) => prev + 1);
    setSearchParams({ category: key }, { replace: true });
  };

  const handleBookNow = (boat) => {
    // Razorpay Link Construction
    const FIXED_PRICE = 2000;
    const url = `https://pages.razorpay.com/yachtmumbai?amount=${FIXED_PRICE}&yacht_name=${encodeURIComponent(
      boat.name
    )}&name=&email=&phone=`;
    window.open(url, "_blank");
  };

  const handleViewDetail = (boat) => {
    // Create slug from name (e.g., 'SM 63' -> 'sm-63')
    const slug = boat.name.toLowerCase().replace(/ /g, "-");
    navigate(`/fleet-detail/${slug}?category=${activeTab}`);
  };

  return (
    <React.Fragment>
      <Header />
      {/* FLEET HERO */}
      <section className="fleet-hero">
        <Container>
          <SectionHead
            as="h1"
            title="Our Fleet in Mumbai"
            lead="Sail boats, sail yachts, motor yachts, speed boats and ferry boats on charter from Gateway of India. Compare capacity and routes below to find the right vessel for your trip."
          />
        </Container>
      </section>

      {/* FLEET TABS */}
      <section className="fleet-nav-tab-section">
        <Container>
          <Tab.Container
            id="fleet-category-tabs"
            activeKey={activeTab}
            onSelect={handleTabSelect}
          >
            <div className="fleet-tabs-header text-center">
              <Nav variant="pills" className="justify-content-center">
                {tabs.map((tab) => (
                  <Nav.Item key={tab.id}>
                    <Nav.Link eventKey={tab.id}>{tab.name}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>

            {/* Category explainer: tells first-time visitors what this class
                of vessel actually is before they scan the cards. */}
            <div className="category-intro" key={`intro-${activeTab}`}>
              <h2 className="category-intro__title">
                {activeCategory.name} in Mumbai
              </h2>
              <p className="category-intro__blurb">{activeCategory.blurb}</p>
              <p className="category-intro__count">
                {currentItems.length}{" "}
                {currentItems.length === 1
                  ? activeCategory.singular.toLowerCase()
                  : activeCategory.name.toLowerCase()}
                {guestRange && <> · seats {guestRange} guests</>}
              </p>
            </div>

            <div className="fleet-tabs-content">
              <Tab.Content>
                <Tab.Pane eventKey={activeTab} key={activeTab}>
                  <div className="row g-4" data-aos="fade-up" key={aosKey}>
                    {currentItems.map((boat) => {
                      const photoCount = boat.gallery ? boat.gallery.length : 0;
                      const routes = boat.trips || [];

                      return (
                        <div
                          key={boat.name}
                          className="col-lg-4 col-md-6 col-12"
                        >
                          <article className="fleet-grid-card">
                            <div className="card-img-wrapper">
                              <img
                                src={boat.image}
                                alt={`${boat.name} ${activeCategory.singular.toLowerCase()} on charter at Gateway of India, Mumbai`}
                                loading="lazy"
                                decoding="async"
                              />
                              {boat.badge && (
                                <span className="badge-overlay">
                                  {boat.badge}
                                </span>
                              )}
                              {photoCount > 0 && (
                                <span className="photo-count">
                                  <i className="ri-image-line"></i> {photoCount}
                                </span>
                              )}
                            </div>

                            <div className="card-content">
                              <span className="card-category">
                                {activeCategory.singular}
                              </span>
                              <h3>{boat.name}</h3>
                              <p className="description">{boat.description}</p>

                              {/* Spec strip — the facts people compare on */}
                              <dl className="spec-strip">
                                <div className="spec">
                                  <dt>
                                    <i className="ri-group-line"></i> Guests
                                  </dt>
                                  <dd>Up to {boat.guests}</dd>
                                </div>
                                <div className="spec">
                                  <dt>
                                    <i className="ri-route-line"></i> Routes
                                  </dt>
                                  <dd>{routes.length || "On request"}</dd>
                                </div>
                              </dl>

                              {routes.length > 0 && (
                                <div className="trip-info">
                                  <span className="trip-info__label">
                                    Departs Gateway of India
                                  </span>
                                  <ul className="trip-list">
                                    {routes.map((trip) => (
                                      <li key={trip}>
                                        {trip.replace(/^Gateway\s*[–-]\s*/, "")}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              <div className="card-actions">
                                <button
                                  type="button"
                                  className="btn-ink btn-sm"
                                  onClick={() => handleViewDetail(boat)}
                                >
                                  View Details
                                </button>
                                <button
                                  type="button"
                                  className="btn-line btn-sm"
                                  onClick={() => handleBookNow(boat)}
                                >
                                  Book Now
                                </button>
                              </div>
                            </div>
                          </article>
                        </div>
                      );
                    })}
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        </Container>
      </section>
    </React.Fragment>
  );
}
